import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const { login, loginLoading } = useAuth();
  const navigate = useNavigate();

  const handleChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const getDashboardByRole = (roleType) => {
    const dashboards = {
      admin: '/executive',        // Executive Dashboard
      manager: '/manager',        // Manager Dashboard
      agent: '/agent',           // Agent Dashboard
      data_uploader: '/data-uploader' // Data Uploader Dashboard
    };
    return dashboards[roleType] || '/dashboard';
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Prevent submission if already loading
    if (loading || loginLoading) {
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        toast.success('Login successful!');
        // Redirect based on user role
        const dashboardPath = getDashboardByRole(result.user.roleType);
        navigate(dashboardPath);
      } else {
        toast.error(result.message || 'Login failed');

        // If rate limited, show additional info
        if (result.retryAfter) {
          setTimeout(() => {
            toast.info('You can try logging in again now');
          }, result.retryAfter * 1000);
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full space-y-8'>
        {/* Header */}
        <div>
          <div className='mx-auto h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center'>
            <span className='text-white font-bold text-lg'>AI</span>
          </div>
          <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
            Sign in to your account
          </h2>
          <p className='mt-2 text-center text-sm text-gray-600'>
            Or{' '}
            <Link to='/register' className='font-medium text-blue-600 hover:text-blue-500'>
              create a new account
            </Link>
          </p>
        </div>

        {/* Form */}
        <form className='mt-8 space-y-6' onSubmit={handleSubmit}>
          <div className='space-y-4'>
            {/* Email */}
            <div>
              <label htmlFor='email' className='label block text-sm font-medium text-gray-700'>
                Email address
              </label>
              <input
                id='email'
                name='email'
                type='email'
                autoComplete='email'
                required
                className='input-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500'
                placeholder='Enter your email'
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor='password' className='label block text-sm font-medium text-gray-700'>
                Password
              </label>
              <input
                id='password'
                name='password'
                type='password'
                autoComplete='current-password'
                required
                className='input-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500'
                placeholder='Enter your password'
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Remember me & Forgot password */}
          <div className='flex items-center justify-between'>
            <div className='flex items-center'>
              <input
                id='remember-me'
                name='remember-me'
                type='checkbox'
                className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
              />
              <label htmlFor='remember-me' className='ml-2 block text-sm text-gray-900'>
                Remember me
              </label>
            </div>

            <div className='text-sm'>
              <Link to='/forgot-password' className='font-medium text-blue-600 hover:text-blue-500'>
                Forgot your password?
              </Link>
            </div>
          </div>

          {/* Submit button */}
          <div>
            <button
              type='submit'
              disabled={loading || loginLoading}
              className='group relative w-full flex justify-center py-2 px-4 border border-transparent
              text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {(loading || loginLoading) ? <LoadingSpinner size='sm' /> : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
